<template>
  <div>
    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4" v-if="isLoading">
      <base-card>
        <div class="tw-flex tw-flex-col tw-items-center">
          <base-skeleton-loader class="tw-h-4 tw-w-24" />
          <base-skeleton-loader class="tw-h-4 tw-w-8 tw-mt-4 tw-mb-4" />
          <base-skeleton-loader class="tw-h-3 tw-w-16 tw-mb-2" />
        </div>
        <div class="tw--mx-4 tw--mb-2.5 sm:tw--mb-4 sm:tw--mx-6 tw-border-t tw-mt-3 tw-divide-x tw-divide-gray-200 tw-grid tw-grid-cols-3">
          <div class="tw-py-2 tw-flex tw-flex-col tw-items-center" v-for="i in 3" :key="i">
            <base-skeleton-loader class="tw-mt-2 tw-h-2 tw-w-12" />
            <base-skeleton-loader class="tw-mt-3 tw-mb-1 tw-h-2 tw-w-8" />
          </div>
        </div>
      </base-card>
    </div>
    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4" v-else>
      <base-card v-for="credit in orderedCreditSummaries" :key="credit.itemCategoryId">
        <div class="tw-flex tw-flex-col tw-items-center">
          <div class="tw-text-xl">{{ credit.itemCategoryName }}</div>
          <div class="tw-text-3xl tw-font-normal tw-text-primary">{{ credit.balance }}</div>
          <div class="tw-text-sm tw-text-gray-500">Available</div>
        </div>
        <div class="tw--mx-4 tw--mb-2.5 sm:tw--mb-4 sm:tw--mx-6 tw-border-t tw-mt-3 tw-divide-x tw-divide-gray-200 tw-grid tw-grid-cols-3">
          <div class="tw-py-2 tw-text-center">
            {{ credit.received }}
            <div class="tw-text-xs tw-text-gray-500">
              Purchased
            </div>
          </div>
          <div class="tw-py-2 tw-text-center">
            {{ credit.redeemed }}
            <div class="tw-text-xs tw-text-gray-500">
              Redeemed
            </div>
          </div>
          <div class="tw-py-2 tw-text-center">
            {{ credit.adjusted }}
            <div class="tw-text-xs tw-text-gray-500">
              Adjusted
            </div>
          </div>
        </div>
      </base-card>
    </div>
    <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin tw-border-b tw-pb-2 tw-mt-4 sm:tw-mt-8">Activity</h1>
    <base-card class="tw-mt-4" no-padding v-if="isLoading">
      <div v-for="i in 3" :key="i" :class="['tw-flex tw-justify-between tw-items-center tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6', { 'tw-border-b': i < 3 }]">
        <div class="tw-flex tw-items-center">
          <base-skeleton-loader class="tw-h-7 tw-w-7 tw-p-1.5 tw-rounded-full" />
          <div class="tw-ml-3.5">
            <base-skeleton-loader class="tw-h-3 tw-w-24 tw-mt-1.5" />
            <base-skeleton-loader class="tw-h-2 tw-w-16 tw-mt-2.5 tw-mb-1.5" />
          </div>
        </div>
        <div>
          <base-skeleton-loader class="tw-w-12 tw-h-3 tw-mt-0.5" />
        </div>
      </div>
    </base-card>
    <base-card class="tw-mt-4" no-padding v-else>
      <div
        v-for="(transaction, index) in orderedCreditTransactions"
        :key="index"
        :class="['tw-flex tw-justify-between tw-items-center tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6', { 'tw-border-b': index + 1 < orderedCreditTransactions.length }]"
      >
        <div class="tw-flex tw-items-center">
          <component
            :is="iconType(transaction.type, transaction.value)"
            :class="['tw-h-7 tw-w-7 tw-p-1.5 tw-rounded-full tw-flex-shrink-0', iconClass(transaction.type, transaction.value)]"
          />
          <div class="tw-ml-3.5">
            <div class="tw--mb-1 sm:tw-mb-0">{{ getTransactionTypeDisplay(transaction.type) }}</div>
            <div class="tw-inline sm:tw-hidden tw--mt-1">
              {{ Math.abs(transaction.value) > 1 ? transaction.itemCategoryName + "s" : transaction.itemCategoryName }}
            </div>
            <div class="tw-text-sm tw-text-gray-600 tw--mt-0.5">{{ formatDate(transaction.date) }}</div>
          </div>
        </div>
        <div class="tw-hidden sm:tw-inline">
          {{ Math.abs(transaction.value) > 1 ? transaction.itemCategoryName + "s" : transaction.itemCategoryName }}
        </div>
      </div>
    </base-card>
  </div>
</template>

<script>
import { GiftIcon, MinusIcon, PlusIcon } from "@/assets/svgs";
import { orderBy } from "lodash";
import moment from "moment";

export default {
  components: {
    GiftIcon,
    MinusIcon,
    PlusIcon,
  },
  props: {
    userId: String,
    userBusinessPlanId: String,
  },
  data() {
    return {
      isLoading: false,
      creditTransactions: [],
      creditSummaries: [],
    };
  },
  computed: {
    orderedCreditSummaries() {
      return orderBy(this.creditSummaries, ["status"]);
    },
    orderedCreditTransactions() {
      return orderBy(this.creditTransactions, ["sortDate"]);
    },
  },
  created() {
    this.EventBus.$on("member_update", this.loadPage);
    this.loadPage();
  },
  beforeDestroy() {
    this.EventBus.$off("member_update", this.loadPage);
  },
  methods: {
    async loadPage() {
      try {
        this.isLoading = true;

        const response = await this.$axios.get(`/users/${this.userId}/userplans/${this.userBusinessPlanId}/history/credits`);
        this.creditTransactions = response.data.creditTransactions;
        this.creditSummaries = response.data.creditSummaries;
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(date) {
      return new moment.utc(date).local().format("MMM Do, YYYY");
    },
    iconType(type, value) {
      switch (type) {
        case "plan_cycle":
        case "recharge":
          return "plus-icon";
        case "adjusted":
          return value >= 0 ? "plus-icon" : "minus-icon";
        case "redeemed":
          return "gift-icon";
        default:
          return;
      }
    },
    iconClass(type, value) {
      switch (type) {
        case "plan_cycle":
        case "recharge":
          return "tw-text-green-600 tw-bg-green-100";
        case "adjusted":
          return value >= 0 ? "tw-text-green-600 tw-bg-green-100" : "tw-text-red-600 tw-bg-red-100";
        case "redeemed":
          return "tw-text-primary-600 tw-bg-primary-100";
        default:
          return;
      }
    },
    getTransactionTypeDisplay(type) {
      switch (type) {
        case "plan_cycle":
          return "Plan Cycle";
        case "recharge":
          return "Recharge";
        case "adjusted":
          return "Adjusted";
        case "redeemed":
          return "Redeemed";
        default:
          return;
      }
    },
  },
};
</script>
